import { Button, Skeleton, Typography } from "antd";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { ApiError, errorMessage } from "../api/client";

/** The part of a react-query result a widget needs to tell its states apart. */
export interface WidgetSource {
  data?: unknown;
  isPending: boolean;
  isError: boolean;
  isFetching?: boolean;
  error: unknown;
  refetch: () => unknown;
}

/**
 * UXW-26 / CW-17: the own states of one widget — loading, a failure with «Повторить» inside the block, a refusal of
 * rights («Доступ изменён»), an empty result. A number is drawn only when it is known: never 0 in place of an unknown
 * or refused value, and a failure of one widget never touches the others.
 */
export function WidgetState({ source, empty, emptyText, children }: { source: WidgetSource; empty?: boolean; emptyText?: ReactNode; children: ReactNode }) {
  const hasData = source.data !== undefined;
  if (source.isError && !hasData) {
    const status = source.error instanceof ApiError ? source.error.problem.status : 0;
    if (status === 403) {
      return (
        <div className="wms-widget-state" data-testid="widget-denied">
          <Typography.Text type="secondary">Доступ изменён: эти данные больше недоступны вашей роли. Обратитесь к администратору.</Typography.Text>
        </div>
      );
    }
    return (
      <div className="wms-widget-state" role="alert" data-testid="widget-error">
        <Typography.Text type="danger">Не удалось обновить: {errorMessage(source.error)}</Typography.Text>
        <Button size="small" onClick={() => source.refetch()} data-testid="widget-retry">
          Повторить
        </Button>
      </div>
    );
  }
  if (source.isPending) {
    return (
      <div className="wms-widget-state" data-testid="widget-loading" aria-busy="true">
        <Skeleton active title={false} paragraph={{ rows: 2 }} />
      </div>
    );
  }
  return (
    <>
      {source.isError && (
        // the last known numbers stay with their time; the block says it could not refresh them
        <div className="wms-widget-stale" role="status" data-testid="widget-stale">
          <Typography.Text type="warning">Не удалось обновить — показаны прежние данные.</Typography.Text>
          <Button size="small" type="link" onClick={() => source.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      {empty ? (
        <div className="wms-widget-state" data-testid="widget-empty">
          <Typography.Text type="secondary">{emptyText ?? "Нет данных"}</Typography.Text>
        </div>
      ) : (
        children
      )}
    </>
  );
}

/** «Данные на HH:mm» of a widget: the moment the server computed the numbers (or the browser got them). */
export function DataTime({ at, fetching }: { at?: string | number | null; fetching?: boolean }) {
  if (!at) return null;
  return (
    <span className="wms-widget-time" data-testid="widget-time">
      Данные на {dayjs(at).format("HH:mm")}
      {fetching ? " · обновляем…" : ""}
    </span>
  );
}
